import { isUndefined } from 'lodash-es';

import { CONFIGURATOR_DEFAULT_CURRENCY, CONFIGURATOR_DEFAULT_LOCALE } from '../constants';
import {
  Campaign,
  CampaignItem,
  CampaignItemType,
  ConfiguratorEngagementCampaignFeature,
  ConfiguratorEngagementHighlight,
  ConfiguratorEngagementHtmlPreview,
  ConfiguratorEngagementItem,
  ConfiguratorEngagementTier,
  ConfiguratorSessionState,
  DefaultItem,
  EngagementOffer,
  TextTranslations,
} from '../types';

export const CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID = 'custom';

export function isGamificationItem<T extends CampaignItem | DefaultItem>(item: T | undefined): item is T {
  return item?.type === CampaignItemType.GAMIFICATION_ITEM;
}

export function getGamificationItems<T extends CampaignItem | DefaultItem>(items: T[] = []): T[] {
  return items.filter((item) => isGamificationItem(item));
}

export function getActiveCampaign(
  campaigns: Campaign[] = [],
  sessionState: ConfiguratorSessionState = {},
): Campaign | undefined {
  if (isUndefined(sessionState) || isUndefined(sessionState.campaignId)) {
    return;
  }

  const existingCampaign = campaigns.find((campaign) => {
    return campaign.id === sessionState.campaignId;
  });

  if (isUndefined(existingCampaign)) {
    return;
  }

  const now = new Date().toISOString();

  const { availability_window } = existingCampaign ?? {};
  const { start_time, end_time } = availability_window ?? {};

  if (start_time <= now && now <= end_time) {
    return existingCampaign;
  }
}

export function getHighlights(tier?: ConfiguratorEngagementTier): ConfiguratorEngagementHighlight[] {
  if (isUndefined(tier)) {
    return [];
  }

  return tier.art_bundle
    .filter((artBundle) => artBundle?.properties?.section_id === 'highlight')
    .map((artBundle) => ({
      title: artBundle?.properties?.title ?? {},
      description: artBundle?.properties?.description ?? {},
    }));
}

export function getHtmlPreview(
  tier: ConfiguratorEngagementTier | undefined,
  itemId: number,
  tierId: number,
): ConfiguratorEngagementHtmlPreview | undefined {
  if (isUndefined(tier)) {
    return;
  }

  const artBundle = tier.art_bundle?.find((ab) => ab?.properties?.section_id === 'html');
  const properties = artBundle?.properties;

  if (isUndefined(artBundle) || isUndefined(properties)) {
    return;
  }

  return {
    ...properties,
    key: `${itemId}_${tierId}`,
    itemId,
    tierId,
    html: properties.page_configurator_preview ?? '',
  };
}

export function getHighlightsHeader(tier?: ConfiguratorEngagementTier): ConfiguratorEngagementHighlight | undefined {
  if (isUndefined(tier)) {
    return;
  }

  const tierArtBundle = tier.art_bundle?.find((artBundle) => artBundle?.properties?.section_id === 'highlightsHeader');

  if (isUndefined(tierArtBundle)) {
    return;
  }

  return {
    title: tierArtBundle?.properties?.title ?? {},
    description: tierArtBundle?.properties?.description ?? {},
  };
}

function pickText(text: TextTranslations | string | undefined, locale: string): string {
  if (typeof text === 'string') return text;
  if (!text) return '';
  return text[locale] ?? text[CONFIGURATOR_DEFAULT_LOCALE] ?? Object.values(text)[0] ?? '';
}

/**
 * Build an EngagementOffer from a campaign's `price_evaluation`. The display `currency`
 * is the selected market's currency (passed in), not the campaign's own currency_code.
 */
export function computeCampaignOffer(
  campaign: Campaign | undefined,
  isCustom = false,
  currency: string = CONFIGURATOR_DEFAULT_CURRENCY,
): EngagementOffer {
  const basePrice = campaign?.price_evaluation?.basePrice ?? 0;
  const discountedPrice = campaign?.price_evaluation?.discountedPrice ?? 0;
  const hasDiscount = discountedPrice > 0 && discountedPrice < basePrice;

  return {
    price: hasDiscount ? discountedPrice : basePrice,
    originalPrice: hasDiscount ? basePrice : undefined,
    currency,
    isCustom,
  };
}

/**
 * Compute the per-feature breakdown for a campaign: label, icon, active tier name,
 * level position within the item's ladder, and a market-reference comparison
 * (middle tier as the reference, matching the market-scan logic).
 */
export function computeCampaignFeatures(
  campaign: Campaign | undefined,
  engagementItems: ConfiguratorEngagementItem[] = [],
  locale: string = CONFIGURATOR_DEFAULT_LOCALE,
): ConfiguratorEngagementCampaignFeature[] {
  if (isUndefined(campaign) || !campaign.items?.length) return [];

  const itemById = new Map<number, ConfiguratorEngagementItem>();
  engagementItems.forEach((item) => itemById.set(item.id, item));

  return campaign.items
    .filter((item) => item.type === CampaignItemType.GAMIFICATION_ITEM)
    .toSorted((a, b) => a.priority - b.priority)
    .reduce<ConfiguratorEngagementCampaignFeature[]>((acc, item) => {
      const defaultDef = campaign.default_items?.find((di) => di.item_id === item.id);
      if (!defaultDef) return acc; // not in this pack

      const activeTier = item.tiers?.find((t) => String(t.id) === String(defaultDef.tier_id));
      if (!activeTier || activeTier.name?.toLowerCase().includes('off')) return acc;

      const ladder = (item.tiers ?? []).filter((t) => !t.name?.toLowerCase().includes('off'));
      const totalLevels = ladder.length;
      const level = ladder.findIndex((t) => String(t.id) === String(activeTier.id)) + 1;
      const progress = totalLevels > 0 ? Math.round((level / totalLevels) * 100) : 0;

      const marketIndex = Math.max(0, Math.floor((totalLevels - 1) / 2));
      const marketTier = ladder[marketIndex];
      const marketLevel = marketIndex + 1;
      const marketProgress = totalLevels > 0 ? Math.round((marketLevel / totalLevels) * 100) : 0;

      const comparison: ConfiguratorEngagementCampaignFeature['comparison'] =
        level > marketLevel ? 'ahead' : level < marketLevel ? 'behind' : 'match';

      const engagementItem = itemById.get(item.id);
      const icon = engagementItem?.art_bundle?.[0]?.logo;

      acc.push({
        id: String(item.id),
        label: pickText(item.name as unknown as TextTranslations, locale),
        icon,
        tierName: pickText(activeTier.name as unknown as TextTranslations, locale),
        level,
        totalLevels,
        progress,
        marketLevel,
        marketTierName: pickText(marketTier?.name as unknown as TextTranslations, locale),
        marketProgress,
        comparison,
      });

      return acc;
    }, []);
}

/**
 * Build the synthetic Custom campaign entry — mirrors the user's current item
 * selection. Returns undefined when there are no session items yet.
 *
 * The base campaign provides currency and item/tier metadata; the items array
 * is rebuilt from the user's session selections so the entry reflects exactly
 * what the user has chosen (independent of any default_items).
 */
export function buildCustomCampaign(
  baseCampaign: Campaign | undefined,
  sessionItems: DefaultItem[] = [],
  customOffer: EngagementOffer | undefined,
): Campaign | undefined {
  if (isUndefined(baseCampaign)) return undefined;

  const gamificationSelections = getGamificationItems(sessionItems);
  if (!gamificationSelections.length) return undefined;

  const basePrice = customOffer?.originalPrice ?? customOffer?.price ?? 0;
  const discountedPrice =
    customOffer?.originalPrice !== undefined ? (customOffer?.price ?? 0) : 0;

  return {
    ...baseCampaign,
    id: CONFIGURATOR_ENGAGEMENT_CUSTOM_CAMPAIGN_ID,
    name: 'Custom',
    default_items: gamificationSelections.map((selection) => ({
      item_id: selection.item_id,
      tier_id: selection.tier_id,
      type: selection.type,
    })),
    price_evaluation: {
      match: false,
      basePrice,
      discountedPrice,
    },
    currency_code: customOffer?.currency ?? baseCampaign.currency_code,
  };
}
